import React, { useEffect, useState } from 'react';

import { Chip } from '@mui/material';

import {
  Encounter,
  EncounterComplication,
  Environment,
} from "@dndModels";

import API from "@utilities/smart_api";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import EditButton from '@components/input/buttons/icon_buttons/EditButton';
import CenteredMenu from "@components/input/menus/CenteredMenu";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import FlexBox from '@components/layout/FlexBox';
import CheckBoxIndex from "@dndComponents/CheckBoxIndex";
import DisplayStringWithReplacementsRecursive from '@dndComponents/display/DisplayStringWithReplacementsRecursive';
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

const api = API.getInstance();

const pickRandom = <T,>(from: T[]): T => from[Math.floor(Math.random() * from.length)];

interface Props {
  encounter: Encounter;
  onChange: (changes: { environment_ids?: string[], complication_ids?: string[] }) => void;
}

// Shows the encounter's Environments and Complications,
// lets you click one to read it, and has the controls for choosing them.
// Reads straight from the encounter, so changes made elsewhere (like Suggest Creatures) show up.
export default function EncounterEnvironmentBar({
  encounter,
  onChange,
}: Props) {
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [complications, setComplications] = useState<EncounterComplication[]>([]);
  const [viewing, setViewing] = useState<Environment | EncounterComplication | null>(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState("Environments");

  useEffect(() => {
    api.getSetOfObjects({ data_types: ["environment","encounter_complication"] }).then(res => {
      setEnvironments((res.environment as Environment[]).sort((a, b) => a.name.localeCompare(b.name)));
      setComplications((res.encounter_complication as EncounterComplication[]).sort((a, b) => a.name.localeCompare(b.name)));
    });
  }, []);

  const environment_ids = encounter.environment_ids;
  const complication_ids = encounter.complication_ids;
  const chosen_environments = environments.filter(o => environment_ids.includes(o._id));
  const chosen_complications = complications.filter(o => complication_ids.includes(o._id));

  // Complications that are for any Environment are always suggested
  const suggested = complications.filter(o => o.suggestedFor(environment_ids));
  // Suggested ones go at the top
  const ordered_complications = [...suggested, ...complications.filter(o => !suggested.includes(o))];

  const roll = (from: EncounterComplication[]) => {
    const rollable = from.filter(o => !complication_ids.includes(o._id));
    if (rollable.length > 0) {
      const rolled = pickRandom(rollable);
      onChange({ complication_ids: [...complication_ids, rolled._id] });
    }
  }

  const suggestedFor = (complication: EncounterComplication) => {
    if (complication.environment_ids.length === 0) {
      return "Suggested for any Environment";
    } else if (suggested.includes(complication)) {
      const names = chosen_environments.filter(o => complication.environment_ids.includes(o._id)).map(o => o.name);
      return `Suggested for Environment(s): ${names.join(", ")}`;
    }
    return "";
  }

  const renderEnvironmentsTab = () => {
    return (
      <CheckBoxIndex
        data_type="environment"
        objects={environments}
        selected_ids={environment_ids}
        onChange={(changed: string[]) => onChange({ environment_ids: changed })}
      />
    );
  }

  const renderComplicationsTab = () => {
    const rollable_suggested = suggested.filter(o => !complication_ids.includes(o._id));
    const rollable_all = complications.filter(o => !complication_ids.includes(o._id));
    return (
      <FlexBox direction="column">
        <FlexBox>
          <SelectObjectBox
            name="Environments"
            data_type="environment"
            values={environment_ids}
            multiple
            onChange={(changed: string[]) => onChange({ environment_ids: changed })}
          />
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox>
            <DandDButtonBox
              name={`Roll from ${rollable_suggested.length} Suggested`}
              disabled={rollable_suggested.length === 0}
              onClick={() => roll(suggested)}
            />
          </FlexBox>
          <FlexBox>
            <DandDButtonBox
              name={`Roll from All ${rollable_all.length}`}
              disabled={rollable_all.length === 0}
              onClick={() => roll(complications)}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox>
          <CheckBoxIndex
            data_type="encounter_complication"
            objects={ordered_complications}
            selected_ids={complication_ids}
            onChange={(changed: string[]) => onChange({ complication_ids: changed })}
            details={suggestedFor}
          />
        </FlexBox>
      </FlexBox>
    );
  }

  const renderEdit = () => {
    return (
      <ModalOrDrawer
        open={editing}
        width={800}
        onClose={() => { setEditing(false); }}>
        <FlexBox direction="column">
          <FlexBox className="header centered">
            Environments & Complications
          </FlexBox>
          <FlexBox>
            <CenteredMenu
              options={["Environments","Complications"]}
              selected={tab}
              onSelect={setTab}
            />
          </FlexBox>
          { tab === "Environments" ? renderEnvironmentsTab() : renderComplicationsTab() }
        </FlexBox>
      </ModalOrDrawer>
    );
  }

  const renderViewing = () => {
    return (
      <ModalOrDrawer
        open={viewing !== null}
        onClose={() => { setViewing(null); }}>
        { viewing &&
          <FlexBox direction="column">
            <FlexBox className="header centered">
              { viewing.name }
            </FlexBox>
            { viewing.image_url !== "" &&
              <FlexBox className="centered">
                <img src={viewing.image_url} alt={viewing.name} style={{ maxWidth: "100%", maxHeight: 300 }} />
              </FlexBox>
            }
            <FlexBox>
              <DisplayStringWithReplacementsRecursive
                display_string={viewing.description}
              />
            </FlexBox>
          </FlexBox>
        }
      </ModalOrDrawer>
    );
  }

  return (
    <FlexBox direction="row" className="padded" style={{ flexWrap: "wrap", alignItems: "center", gap: 4 }}>
      <b>Environments:</b>
      { chosen_environments.length === 0 && <span className="gray">None</span> }
      { chosen_environments.map(o => (
        <Chip key={o._id} label={o.name} size="small" color="primary" variant="outlined"
          onClick={() => setViewing(o)}
        />
      ))}
      <b style={{ marginLeft: 12 }}>Complications:</b>
      { chosen_complications.length === 0 && <span className="gray">None</span> }
      { chosen_complications.map(o => (
        <Chip key={o._id} label={o.name} size="small" color="warning" variant="outlined"
          onClick={() => setViewing(o)}
        />
      ))}
      <EditButton
        no_fab
        onClick={() => { setEditing(true); }}
      />
      { renderEdit() }
      { renderViewing() }
    </FlexBox>
  );
}
